/**
 * use-ops — ops API hooks (ADM-OPS-S001~S009)
 */

import type {
  AppDedicatedConfigRequest,
  BannerResponse,
  BannerSlideResponse,
  BannersResponse,
  ConfigLanguageTabModule,
  ConfigLanguageTabsResponse,
  CreateBannerRequest,
  CreateBannerSlideRequest,
  CreateFaqCategoryRequest,
  CreateFaqRequest,
  CreateHomeSectionNovelRequest,
  CreateOperationalPopupSceneRequest,
  CreateShelfRecommendationRequest,
  FaqCategoryResponse,
  FaqResponse,
  FaqsResponse,
  HomeSectionNovelResponse,
  HomeSectionResponse,
  HomeSectionStrategyResponse,
  HomeSectionsResponse,
  OperationalPopupSceneResponse,
  PopupPlacementResponse,
  PopupScenesResponse,
  RemoveAppDedicatedConfigResponse,
  ShelfRecommendationResponse,
  ShelfRecommendationsResponse,
  UpdateBannerRequest,
  UpdateBannerSlideRequest,
  UpdateFaqCategoryRequest,
  UpdateFaqRequest,
  UpdateHomeSectionNovelRequest,
  UpdateHomeSectionRequest,
  UpdateOperationalPopupSceneRequest,
  UpdateShelfRecommendationRequest,
} from '@novel-admin/shared';
import { apiRequest, apiRequestOffsetPaged } from '~/api/client';
import { uploadImageFile } from '~/api/upload-image';

export const opsApi = {
  listSections: (appId: string | null, language: string) =>
    apiRequest<HomeSectionsResponse>({
      method: 'GET',
      path: '/api/v1/ops/home-sections',
      query: { ...(appId ? { app_id: appId } : {}), language },
    }),
  // 推荐策略注册表（GET /ops/home-section-strategies，viewer+ 只读）：返回全集（含 enabled=false）；
  // BlockDetail 推荐策略下拉据此填充（只列 enabled=true 的可选策略，见 useHomeSectionStrategies）。
  listHomeSectionStrategies: () =>
    apiRequest<HomeSectionStrategyResponse[]>({
      method: 'GET',
      path: '/api/v1/ops/home-section-strategies',
    }),
  updateSection: (id: string, body: UpdateHomeSectionRequest) =>
    apiRequest<HomeSectionResponse>({
      method: 'PATCH',
      path: `/api/v1/ops/home-sections/${id}`,
      body,
    }),
  copyDedicatedConfig: (body: AppDedicatedConfigRequest) =>
    apiRequest<HomeSectionsResponse>({
      method: 'POST',
      path: '/api/v1/ops/app-dedicated-configs',
      body,
    }),
  removeDedicatedConfig: (query: AppDedicatedConfigRequest) =>
    apiRequest<RemoveAppDedicatedConfigResponse>({
      method: 'DELETE',
      path: '/api/v1/ops/app-dedicated-configs',
      query,
    }),
  listSectionNovels: (sectionId: string, locale?: string) =>
    apiRequest<HomeSectionNovelResponse[]>({
      method: 'GET',
      path: `/api/v1/ops/home-sections/${sectionId}/novels`,
      query: locale ? { locale } : {},
    }),
  createSectionNovel: (sectionId: string, body: CreateHomeSectionNovelRequest) =>
    apiRequest<HomeSectionNovelResponse>({
      method: 'POST',
      path: `/api/v1/ops/home-sections/${sectionId}/novels`,
      body,
    }),
  updateSectionNovel: (sectionId: string, bindingId: string, body: UpdateHomeSectionNovelRequest) =>
    apiRequest<HomeSectionNovelResponse>({
      method: 'PATCH',
      path: `/api/v1/ops/home-sections/${sectionId}/novels/${bindingId}`,
      body,
    }),
  deleteSectionNovel: (sectionId: string, bindingId: string) =>
    apiRequest<{ id: string; deleted_at: string }>({
      method: 'DELETE',
      path: `/api/v1/ops/home-sections/${sectionId}/novels/${bindingId}`,
    }),

  // 统一语言 Tab（A2 §0.2）：候选 = enabled 去掉 zh。App 视图须带 module。
  listLanguageTabs: (opts: { appId?: string | null; module?: ConfigLanguageTabModule | null }) =>
    apiRequest<ConfigLanguageTabsResponse>({
      method: 'GET',
      path: '/api/v1/ops/config-language-tabs',
      query: {
        ...(opts.appId ? { app_id: opts.appId } : {}),
        ...(opts.appId && opts.module ? { module: opts.module } : {}),
      },
    }),

  // banners：language 必填；单层 envelope（A2 §3.1 / §0.3）
  listBanners: (appId: string | null, language: string) =>
    apiRequest<BannersResponse>({
      method: 'GET',
      path: '/api/v1/ops/banners',
      query: { language, ...(appId ? { app_id: appId } : {}) },
    }),
  // 新建 Banner 位（A2 §3.2）：POST 语义 —— 同层 slug 撞行报 RESOURCE_CONFLICT，不像 PUT-upsert
  // 那样静默改掉另一条既有位。is_active / is_seed_baseline 由服务端定，不从前端传。
  createBanner: (body: CreateBannerRequest) =>
    apiRequest<BannerResponse>({ method: 'POST', path: '/api/v1/ops/banners', body }),
  /** 改 Banner 位 / 上下架（A2 §3.3）：name / display_order / is_active 任意子集；层由 :id 所在行决定 */
  updateBanner: (id: string, body: UpdateBannerRequest) =>
    apiRequest<BannerResponse>({ method: 'PATCH', path: `/api/v1/ops/banners/${id}`, body }),
  deleteBanner: (id: string) =>
    apiRequest<{ ok: true }>({ method: 'DELETE', path: `/api/v1/ops/banners/${id}` }),

  listSlides: (bannerId: string, language: string) =>
    apiRequest<BannerSlideResponse[]>({
      method: 'GET',
      path: `/api/v1/ops/banners/${bannerId}/slides`,
      query: { language },
    }),
  /** 新建轮播图（A2 §4.2）：服务端定 is_active=false，故不带该字段 */
  createSlide: (bannerId: string, body: CreateBannerSlideRequest) =>
    apiRequest<BannerSlideResponse>({
      method: 'POST',
      path: `/api/v1/ops/banners/${bannerId}/slides`,
      body,
    }),
  /**
   * 改轮播图 / 上下架（A2 §4.3）：任意子集。**不带 `language`** —— 改它等于把该行搬到另一
   * 语言，契约明列为不可改，换语言走新建。
   */
  updateSlide: (bannerId: string, slideId: string, body: UpdateBannerSlideRequest) =>
    apiRequest<BannerSlideResponse>({
      method: 'PATCH',
      path: `/api/v1/ops/banners/${bannerId}/slides/${slideId}`,
      body,
    }),
  // Banner 轮播图直传（ADM-OPS-S004）：multipart → 返 image_r2_key（随后随 upsertSlide 落库）。走共享 uploadImageFile
  // （apiRequest 只发 JSON、破坏 multipart boundary）。写共享内容桶 banners/{id}/slides/{assetId}.webp。
  uploadSlideImage: (bannerId: string, file: File): Promise<{ image_r2_key: string }> =>
    uploadImageFile<{ image_r2_key: string }>(`/api/v1/ops/banners/${bannerId}/slide-image`, file),
  deleteSlide: (bannerId: string, slideId: string) =>
    apiRequest<{ ok: true }>({
      method: 'DELETE',
      path: `/api/v1/ops/banners/${bannerId}/slides/${slideId}`,
    }),

  // FAQ canonical（#221）：faq 升为 C 端 own 可配置表，global ∪ per-App override（app_id null=全局默认层），
  // REST CRUD 取代旧 PUT upsert 单数 /faq。列表 offset 分页（envelope 带 total/page/limit）。
  // app_id 省略 = 全局层（app_id IS NULL）；传 UUID = 该 App 覆盖 / 追加层。
  listFaqs: (appId: string | null, language: string) =>
    apiRequest<FaqsResponse>({
      method: 'GET',
      path: '/api/v1/ops/faqs',
      query: {
        ...(appId ? { app_id: appId } : {}),
        language,
      },
    }),
  createFaq: (body: CreateFaqRequest) =>
    apiRequest<FaqResponse>({ method: 'POST', path: '/api/v1/ops/faqs', body }),
  updateFaq: (id: string, body: UpdateFaqRequest) =>
    apiRequest<FaqResponse>({ method: 'PATCH', path: `/api/v1/ops/faqs/${id}`, body }),
  // 软删响应 { id, deleted_at }（A2 §9.4）；调用方通常忽略 body，仅关心成功与否。
  deleteFaq: (id: string) =>
    apiRequest<{ id: string; deleted_at: string }>({
      method: 'DELETE',
      path: `/api/v1/ops/faqs/${id}`,
    }),

  // FAQ 分类注册表（faq_categories，global-only 可配置）：plain array GET（非分页）+ REST CRUD。
  listFaqCategories: () =>
    apiRequest<FaqCategoryResponse[]>({ method: 'GET', path: '/api/v1/ops/faq-categories' }),
  createFaqCategory: (body: CreateFaqCategoryRequest) =>
    apiRequest<FaqCategoryResponse>({ method: 'POST', path: '/api/v1/ops/faq-categories', body }),
  updateFaqCategory: (id: string, body: UpdateFaqCategoryRequest) =>
    apiRequest<FaqCategoryResponse>({
      method: 'PATCH',
      path: `/api/v1/ops/faq-categories/${id}`,
      body,
    }),
  // 软删响应 { id, deleted_at }（A2 §10.4）；被 active faq 引用时后端返 OPS_FAQ_CATEGORY_IN_USE。
  deleteFaqCategory: (id: string) =>
    apiRequest<{ id: string; deleted_at: string }>({
      method: 'DELETE',
      path: `/api/v1/ops/faq-categories/${id}`,
    }),
  // initAppOps（S006）已退役：dev-docs ADM-OPS-S006「弃用」，endpoint 已删（codex adversarial #23）。

  // 书架推荐（A2 §8）：app_id 省略 = 全局层；给出 = 该 App 层。language 按 novels.locale 精确取行。
  listShelfRecommendations: (appId: string | null | undefined, language: string) =>
    apiRequest<ShelfRecommendationsResponse>({
      method: 'GET',
      path: '/api/v1/ops/shelf-recommendations',
      query: {
        ...(appId ? { app_id: appId } : {}),
        language,
      },
    }),
  /** 加入推荐（A2 §8.2）：服务端定 is_listed=false，建后再 PATCH 上架 */
  createShelfRecommendation: (body: CreateShelfRecommendationRequest) =>
    apiRequest<ShelfRecommendationResponse>({
      method: 'POST',
      path: '/api/v1/ops/shelf-recommendations',
      body,
    }),
  /** 改排序 / 上下架（A2 §8.3）：sort_order / is_listed 任意子集；不可改 app_id / novel_id */
  updateShelfRecommendation: (id: string, body: UpdateShelfRecommendationRequest) =>
    apiRequest<ShelfRecommendationResponse>({
      method: 'PATCH',
      path: `/api/v1/ops/shelf-recommendations/${id}`,
      body,
    }),
  deleteShelfRecommendation: (id: string) =>
    apiRequest<{ ok: true }>({
      method: 'DELETE',
      path: `/api/v1/ops/shelf-recommendations/${id}`,
    }),

  // 运营弹窗场景（S007，C 端 own 镜像）—— canonical CRUD，dev-docs `02-API契约/A2-运营配置.md` §5。
  // 两层承载（§5.1 单层选取）：省 app_id = 全局层（app_id IS NULL）；给 app_id = 该 App 有未软删行则只读
  // 该层、零行则 config_source=GLOBAL_INHERITED 且 items 恒 []（不回传全局行、两层不并集）。
  // 🚫 无语言维度（§5 头部，张培 dev-docs#733 PR733-007 拍板）：本组端点不接受 `language`，传入即
  //    VALIDATION_FAILED —— 一个 App 的上下架对其全部语言一致，故这里没有 language 入参。
  // 写档 root/admin（§5.2/§5.3/§5.4 admin+ mfa；operator 只读，与其余 ops 模块的 upsert 档不同）。
  listPopupScenes: (appId: string | null) =>
    apiRequest<PopupScenesResponse>({
      method: 'GET',
      path: '/api/v1/ops/popup-scenes',
      // 全局层 = 省略 app_id（不是传空串）
      query: appId ? { app_id: appId } : {},
    }),
  /** 新建场景（§5.2）：服务端定 `is_active=false`，故本请求不带该字段 */
  createPopupScene: (body: CreateOperationalPopupSceneRequest) =>
    apiRequest<OperationalPopupSceneResponse>({
      method: 'POST',
      path: '/api/v1/ops/popup-scenes',
      body,
    }),
  /** 改上下架（§5.3）：按 :id 寻址，层由该行自身 app_id 决定；只传要改的字段，不做全字段覆盖 */
  updatePopupScene: (id: string, body: UpdateOperationalPopupSceneRequest) =>
    apiRequest<OperationalPopupSceneResponse>({
      method: 'PATCH',
      path: `/api/v1/ops/popup-scenes/${id}`,
      body,
    }),
  deletePopupScene: (id: string) =>
    apiRequest<{ id: string; deleted_at: string }>({
      method: 'DELETE',
      path: `/api/v1/ops/popup-scenes/${id}`,
    }),

  // 运营弹窗统一 Paywall 落点（S008，dev-docs `02-API契约/A2-运营配置.md` §6）—— 只读。
  // app_payment_methods 中 `scenario='operational_popup'` 的行，承载 adapty_placement_id，是 S007 各 active
  // 场景的唯一 serving 落点。写入 / 清空走支付页 `PUT /payment-pages/:id/paywalls`（A8 §7.5，paymentPagesApi）。
  listPopupPlacements: (appId: string) =>
    apiRequestOffsetPaged<PopupPlacementResponse>({
      method: 'GET',
      path: '/api/v1/ops/popup-placements',
      // 渠道闭集只有两行，limit 取 offset 分页默认档即可覆盖全量（不做翻页 UI）
      query: { app_id: appId, page: 1, limit: 50 },
    }),
};
